import { faker } from "@faker-js/faker/locale/id_ID";
import { type Privilege, privileges } from "../privilege.common";
import type { InvoiceItem, MikrotikParam } from "../types.common";
import { FakeBase } from "./base.fakes";

export class FakeDevelopment extends FakeBase {
    public override get random(): {
        string: () => string;
        number: () => number;
        rating: () => number;
        phrase: () => string;
        select: <T>(selection: T[]) => () => T;
        selectMany: <T>(selection: T[]) => () => T[];
        selectFn: <T>(selection: T[]) => T;
        selectManyFn: <T>(selection: T[]) => T[];
    } {
        return {
            string: () => faker.string.alpha({ length: 8, casing: "upper" }),
            number: () => faker.number.int({ min: 1, max: 100 }),
            rating: () => faker.number.int({ min: 1, max: 10 }),
            phrase: () => faker.lorem.sentence(),
            select: (selection) => () => faker.helpers.arrayElement(selection),
            selectMany: (selection) => () => faker.helpers.arrayElements(selection),
            selectFn: (selection) => faker.helpers.arrayElement(selection),
            selectManyFn: (selection) => faker.helpers.arrayElements(selection),
        };
    }

    public override get person(): {
        name: () => string;
        email: () => string;
        username: () => string;
        password: () => string;
        phone: () => string;
        idnum: () => string;
        privilegeList: (privilegeMap: Privilege.Map) => () => Record<Privilege.Names, boolean>;
    } {
        return {
            name: () => {
                const name = faker.person.fullName();
                const part = name.split(" ").map((p) => p.trim());
                if (part.length >= 3) {
                    return `${part[0]} ${part[2]}`;
                } else {
                    return name;
                }
            },
            email: () => faker.internet.email({ allowSpecialCharacters: false }).toLowerCase(),
            username: () => faker.string.alpha({ length: 8, casing: "lower" }),
            password: () => "secret",
            phone: () => "+628" + faker.string.numeric({ length: { min: 10, max: 10 } }),
            idnum: () => faker.string.numeric({ length: 16 }),
            privilegeList: (privilegeMap) => () => {
                const map = privileges.create(privilegeMap);
                faker.helpers.arrayElements(Object.keys(map)).forEach((k) => (map[k] = true));
                return map;
            },
        };
    }

    public override get company(): {
        name: () => string;
        position: () => string;
        privilegeName: () => string;
    } {
        return {
            name: () => `RingNet ${faker.location.state()}`,
            position: () => faker.person.jobTitle(),
            privilegeName: () => faker.person.jobType(),
        };
    }

    public override get location(): {
        name: () => string;
        address: () => string;
        coordinate: () => string;
    } {
        return {
            name: () => faker.location.city(),
            address: () => faker.location.streetAddress(),
            coordinate: () => {
                const coord = faker.location
                    .nearbyGPSCoordinate({
                        origin: [-7.329570911668107, 108.23963820934297],
                        radius: 100 / 1000,
                        isMetric: true,
                    })
                    .map((p) => p.toString());

                return `${coord[0]}, ${coord[1]}`;
            },
        };
    }

    public override get pop(): {
        radius: () => number;
        capacity: () => number;
    } {
        return {
            radius: () => faker.number.int({ min: 1, max: 50, multipleOf: 1 }),
            capacity: () => faker.number.int({ min: 1, max: 100, multipleOf: 1 }),
        };
    }

    public override get account(): {
        name: () => string;
        bank: () => string;
        number: () => string;
        initialBalance: () => number;
    } {
        return {
            name: () => faker.finance.accountName(),
            bank: () => faker.finance.creditCardIssuer(),
            number: () => faker.finance.accountNumber(),
            initialBalance: () => faker.number.int({ min: 0, max: 1_000_000, multipleOf: 100_000 }),
        };
    }

    public override get network(): {
        ipv4: () => string;
        cidr: () => number;
        port: () => number;
    } {
        return {
            ipv4: () => faker.internet.ipv4({ cidrBlock: "192.168.0.0/16" }),
            cidr: () => faker.number.int({ min: 20, max: 30, multipleOf: 1 }),
            port: () => faker.number.int({ min: 1025, max: 65535, multipleOf: 1 }),
        };
    }

    public override get product(): {
        name: () => string;
        code: () => string;
        price: () => number;
    } {
        return {
            name: () => {
                const name = faker.animal.petName();
                const speed = faker.number.int({ min: 10, max: 50, multipleOf: 5 });
                return `${name} ${speed} Mbps`;
            },
            code: () => faker.string.alphanumeric({ casing: "upper", length: { min: 8, max: 12 } }),
            price: () => faker.number.int({ min: 100_000, max: 600_000, multipleOf: 10_000 }),
        };
    }

    public override get invoice(): {
        tax: () => number;
        products: () => InvoiceItem[];
    } {
        return {
            tax: () => faker.number.int({ min: 5, max: 12, multipleOf: 1 }),
            products: () => {
                const generate = () => {
                    const price = this.product.price();
                    const quantity = faker.number.int({ min: 1, max: 10, multipleOf: 1 });

                    return {
                        name: this.product.name(),
                        price: price,
                        quantity: quantity,
                        discount: 0,
                        taxType: "none",
                        total: price * quantity,
                    } as const;
                };

                const result = [];
                const count = faker.number.int({ min: 1, max: 5, multipleOf: 1 });
                for (let i = 0; i < count; i++) {
                    result.push(generate());
                }

                return result;
            },
        };
    }

    public override get hotspot(): {
        name: () => string;
        price: () => number;
        profit: () => number;
        dnsName: () => string;
        quantity: () => number;
        codeLength: () => number;
        timeLimit: () => number;
        userLimit: () => number;
        activeDays: () => number;
    } {
        return {
            name: () => {
                const time = faker.number.int({ min: 1, max: 24, multipleOf: 1 });
                return `VOUCHER ${time} JAM`;
            },
            price: () => faker.number.int({ min: 5000, max: 10000, multipleOf: 1000 }),
            profit: () => faker.number.int({ min: 1000, max: 3000, multipleOf: 500 }),
            dnsName: () => faker.internet.domainName(),
            quantity: () => faker.number.int({ min: 2, max: 8, multipleOf: 1 }),
            codeLength: () => faker.number.int({ min: 3, max: 6, multipleOf: 1 }),
            timeLimit: () => faker.number.int({ min: 5, max: 60, multipleOf: 5 }),
            userLimit: () => faker.number.int({ min: 1, max: 5, multipleOf: 1 }),
            activeDays: () => faker.number.int({ min: 7, max: 60, multipleOf: 1 }),
        };
    }

    public override get nas(): {
        name: () => string;
        secret: () => string;
    } {
        return {
            name: () => faker.location.city(),
            secret: () => faker.string.alphanumeric({ length: 6, casing: "lower" }),
        };
    }

    public override get radius(): {
        name: () => string;
        profile: () => string;
        timeLimit: () => number;
        quotaLimit: () => number;
        speedLimit: () => number;
        username: () => string;
        password: () => string;
        params: () => MikrotikParam;
    } {
        const genCreds = () => faker.string.alphanumeric({ length: 8, casing: "lower" });
        const credsSuffix = "@ring.net.id";

        const genSpeed = () => faker.number.int({ min: 1000, max: 200000, multipleOf: 1000 });
        const genDuration = () => faker.number.int({ min: 10, max: 360, multipleOf: 1 });

        return {
            name: () => faker.word.noun({ strategy: "any-length" }),
            profile: () => "default",
            timeLimit: () => faker.number.int({ min: 1_000, max: 86_400, multipleOf: 500 }),
            quotaLimit: () => faker.number.int({ min: 1_000, max: 200_000, multipleOf: 1_000 }),
            speedLimit: () => faker.number.int({ min: 1_000, max: 100_000, multipleOf: 1_000 }),
            username: () => genCreds() + credsSuffix,
            password: () => genCreds() + credsSuffix,
            params: () => ({
                isActive: true,
                mikrotikProfile: faker.string.alphanumeric({ length: 8, casing: "lower" }),
                uploadSpeed: genSpeed(),
                downloadSpeed: genSpeed(),
                useBurst: faker.helpers.arrayElement([true, false]),
                uploadBurstLimit: genSpeed(),
                downloadBurstLimit: genSpeed(),
                uploadBurstThreshold: genSpeed(),
                downloadBurstThreshold: genSpeed(),
                uploadBurstTime: genDuration(),
                downloadBurstTime: genDuration(),
                priority: 8,
                uploadLimitAt: 0,
                downloadLimitAt: 0,
            }),
        };
    }

    public override get item(): {
        name: () => string;
        group: () => string;
        category: () => string;
    } {
        return {
            name: () => faker.commerce.product(),
            group: () => faker.commerce.product(),
            category: () => faker.commerce.product(),
        };
    }
}
